import React, { forwardRef, useState, useImperativeHandle, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useLockFn, useUnmount } from 'ahooks';
import { Button, Dialog } from 'antd-mobile';
import { Icon } from '@fuxi/eevee-icon';
import service from '@/service';
import { selectCurrentServingOrder } from '@/store/service';
import { storagePrefixV2 } from '@/constant';
import SlideModal from '../slide-modal';
import SuggestionsForFeedback from '../suggestions-for-feedback';
import cx from './index.module.scss';

interface Props {
  onCheckArmPass?: (deviceInfo: DeviceItem) => void;
  deviceInfo?: DeviceItem;
}

export interface CheckArmRefAttrs {
  show: VoidFunc;
  hide: VoidFunc;
  setBtnEnabled: VoidFunc;
  setBtnDisabled: VoidFunc;
  setIsShowClose: React.Dispatch<React.SetStateAction<boolean>>;
  handleCheckArm: (deviceInfo: DeviceItem) => Promise<any>;
  setExecutorType: (executorType: Massage.ExecutorType) => void;
  setMassageProcess: React.Dispatch<React.SetStateAction<HelpPhysiotherapyStage>>;
}

const MASSAGE_HEAD_TYPE_TEXT: Record<Massage.ExecutorType, string> = {
  SuspensionMoxibustion: '矩阵艾灸',
  NoneType: '理疗末端',
};

const MASSAGE_HEAD_TYPE: Record<Massage.ExecutorType, string> = {
  SuspensionMoxibustion: '矩阵艾灸头',
  NoneType: '理疗末端',
};

enum ImgSrc {
  'SuspensionMoxibustion' = storagePrefixV2 + '/operation-note/moxibustion.png',
  'NoneType' = storagePrefixV2 + '/operation-note/none-type.png',
}

const CheckArm = forwardRef<CheckArmRefAttrs, Props>(({ onCheckArmPass, deviceInfo }, ref) => {
  const currentServingOrder = useSelector(selectCurrentServingOrder);
  const [isShowClose, setIsShowClose] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isConfirmBtnDisabled, setIsConfirmBtnDisabled] = useState(true);
  const [isConfirmBtnLoading, setIsConfirmBtnLoading] = useState(false);
  const currentDeviceInfo = useRef<DeviceItem>(null);
  const loopIntervalTimer = useRef<NodeJS.Timeout>(null);
  /** 兼容矩阵式艾灸 颈部按摩功能 */
  const [massageType, setMassageType] = useState<Massage.ExecutorType>('NoneType');
  /** 控制弹窗弹出动画 */
  const [isShow, setIsShow] = useState<boolean>(false);
  const [massageProcess, setMassageProcess] = useState<HelpPhysiotherapyStage>(null);

  useUnmount(() => {
    clearInterval(loopIntervalTimer.current);
  });

  const loopGetMoxCheckState = useCallback((deviceId: string) => {
    loopIntervalTimer.current = setInterval(async () => {
      const data = await service.lanInterface.getCollisionCheckState(deviceId);

      const result = data?.state;
      const ExecutorType = data?.executorType;
      /** 判断是颈部按摩 or 矩阵式艾灸 */
      setMassageType(ExecutorType || 'SuspensionMoxibustion');

      if (result === 'Activated_Passed') {
        setIsConfirmBtnDisabled(false);
        clearInterval(loopIntervalTimer.current);
      }
    }, 1000);
  }, []);

  const handleCheckArm = useLockFn(
    useCallback(
      (deviceInfo: DeviceItem) => {
        currentDeviceInfo.current = deviceInfo;
        return new Promise<any>(async (resolve, reject) => {
          // 人工单不进行预检
          if (currentServingOrder?.onlyManual) {
            resolve('');
            onCheckArmPass?.(deviceInfo);
            return;
          }

          try {
            const data2 = await service.lanInterface.collisionPreflight(deviceInfo.deviceId);
            const result = data2?.state;

            const ExecutorType = data2?.executorType;
            /** 判断是颈部按摩 or 矩阵式艾灸 */
            setMassageType(ExecutorType || 'SuspensionMoxibustion');
            if (result === 'Inactive') {
              resolve('');
              onCheckArmPass?.(deviceInfo);
            } else {
              data2?.error === 'Visual' && setIsShowClose(true);
              result === 'Activated_Passed' && setIsConfirmBtnDisabled(false);
              toggleShow(true);
              loopGetMoxCheckState(deviceInfo.deviceId);
              reject(data2);
            }
          } catch (e) {
            reject(e);
          }
        });
      },
      [currentServingOrder, loopGetMoxCheckState, onCheckArmPass]
    )
  );

  const toggleConfirmBtnDisabled = useCallback(
    (flag: boolean) => () => {
      setIsConfirmBtnDisabled(flag);
    },
    []
  );

  const toggleShow = (flag: boolean) => {
    if (flag) {
      setIsShow(true);
      setTimeout(() => {
        setIsDrawerOpen(true);
      }, 100);
    } else {
      setIsDrawerOpen(false);
      setTimeout(() => {
        setIsShow(false);
      }, 100);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      handleCheckArm,
      show: () => toggleShow(true),
      hide: () => toggleShow(false),
      setExecutorType: (executorType: Massage.ExecutorType) => setMassageType(executorType || 'SuspensionMoxibustion'),
      setBtnEnabled: toggleConfirmBtnDisabled(false),
      setBtnDisabled: toggleConfirmBtnDisabled(true),
      setIsShowClose,
      setMassageProcess,
    }),
    [handleCheckArm, toggleConfirmBtnDisabled]
  );

  const handleConfirmRemoveMassageHead = useCallback(
    async (config?: { isForce: boolean }) => {
      const { isForce = false } = config || {};

      setIsConfirmBtnLoading(true);

      try {
        if (isForce) {
          await service.lanInterface.forceCloseCollisionCheck(
            currentDeviceInfo.current?.deviceId || currentServingOrder?.deviceId || deviceInfo?.deviceId
          );
        } else {
          await service.lanInterface.closeCollisionCheck(
            currentDeviceInfo.current?.deviceId || currentServingOrder?.deviceId || deviceInfo?.deviceId
          );
        }
        toggleShow(false);
        onCheckArmPass?.(currentDeviceInfo.current);
      } catch (e) {
        console.error(e);
      } finally {
        setIsConfirmBtnLoading(false);
      }
    },
    [currentServingOrder, deviceInfo, onCheckArmPass]
  );

  const handleForceClose = useCallback(() => {
    Dialog.confirm({
      title: '确认要手动关闭吗？',
      content: '为了确保安全，请确认按摩头已取下。',
      confirmText: '确认',
      onConfirm: async () => {
        handleConfirmRemoveMassageHead({ isForce: true });
      },
    });
  }, [handleConfirmRemoveMassageHead]);

  return (
    isShow &&
    ReactDOM.createPortal(
      <SlideModal className={cx('modal-wrapper', { show: isDrawerOpen })} open={isDrawerOpen}>
        <div className={cx('modal-box')}>
          <div className={cx('head')}>
            <div className={cx('title')}>
              <Icon name="warning-triangle" className={cx('icon')} />
              <span>{MASSAGE_HEAD_TYPE_TEXT[massageType]}</span>
              <span>的操作注意事项</span>
            </div>
            <div className={cx('right')}>
              {/* 只有弹窗弹出时埋点下单前埋点 */}
              {isDrawerOpen && massageProcess && <SuggestionsForFeedback currentProcess={massageProcess} />}
              {isShowClose && <Icon className={cx('icon')} name="close-drawer" onClick={handleForceClose} />}
            </div>
          </div>
          <div className={cx('content')}>
            <img src={ImgSrc[massageType]} alt="physiotherapy-head" />
            <div className={cx('note')}>
              <span>为确保安全，</span>
              <span>请留意{MASSAGE_HEAD_TYPE[massageType]}是否取下，否则会撞击机械臂造成伤害，</span>
              <br />
              <span>同时观察摄像头是否被遮挡，光照是否充足。</span>
            </div>
            <div className={cx('bottom')}>
              <Button
                color="primary"
                className={cx('btn')}
                loading={isConfirmBtnLoading}
                onClick={() => handleConfirmRemoveMassageHead()}
                disabled={isConfirmBtnDisabled}
              >
                {isConfirmBtnDisabled
                  ? `确认${MASSAGE_HEAD_TYPE[massageType] || ''}已取下`
                  : `已确认取下${MASSAGE_HEAD_TYPE[massageType] || ''}`}
              </Button>
            </div>
          </div>
        </div>
      </SlideModal>,
      document.body
    )
  );
});

export default CheckArm;
